// app/layout.tsx
'use client';

import { Inter } from 'next/font/google'
import { AuthProvider } from '@/context/AuthContext'
import LoginModal from '@/components/LoginModal'
import { useAuth } from '@/context/AuthContext'
import { useEffect, useState, ReactNode } from 'react'
import { useRouter, usePathname } from 'next/navigation'
import ToastProvider from '@/components/ToastProvider'
import Image from 'next/image';
import './globals.css'

const inter = Inter({ subsets: ['latin'] })

interface AuthWrapperProps {
  children: ReactNode;
}

const getRedirectPathByRole = (role?: string | null) => {
  const normalizedRole = String(role || "")
    .toLowerCase()
    .replace(/_/g, "-");

  if (normalizedRole === "director-tpv") {
    return "/director-dashboard";
  }

  return "/";
};

// app/layout.tsx - Simplified AuthWrapper
function AuthWrapper({ children }: AuthWrapperProps) {
  const { user, loading } = useAuth()
  const [showLoginModal, setShowLoginModal] = useState(false)
  const router = useRouter()
  const pathname = usePathname()

  useEffect(() => {
    if (loading) return;

    if (!user) {
      setShowLoginModal(true);
      return;
    }

    setShowLoginModal(false);

    const redirectPath = getRedirectPathByRole(user.role);

    // Only auto-redirect from the home page.
    // This prevents the user from being forced back when navigating inside the dashboard.
    if (redirectPath !== "/" && pathname === "/") {
      router.replace(redirectPath);
    }
  }, [user, loading, pathname, router]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 to-primary/10 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="flex items-center justify-center">
            <Image
              src="/CUY.jpg"
              alt="Urban Council Logo"
              width={100}
              height={100}
              className="object-cover"
              priority
            />
          </div>
          <h1 className="text-2xl font-bold text-slate-800">Urban Council Admin</h1>
          <p className="text-slate-600 mt-2">Loading...</p>
        </div>
      </div>
    )
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 to-primary/10 flex items-center justify-center p-4">
        <LoginModal
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
        />
        <div className="text-center">
          <div className="size-16 rounded-xl bg-gradient-to-br from-primary to-primary-dark flex items-center justify-center text-white mx-auto mb-4">
            <span className="font-bold text-xl">Y</span>
          </div>
          <h1 className="text-2xl font-bold text-slate-800">Urban Council Admin</h1>
          <p className="text-slate-600 mt-2">Please login to continue</p>
        </div>
      </div>
    )
  }

  return <>{children}</>;
}

interface RootLayoutProps {
  children: ReactNode;
}

export default function RootLayout({ children }: RootLayoutProps) {
  return (
    <html lang="en">
      <body className={inter.className}>
        <AuthProvider>
          <AuthWrapper>
            {children}
          </AuthWrapper>
          <ToastProvider />
        </AuthProvider>
      </body>
    </html>
  )
}